import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../AxiosInstance';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { trackPurchaseDeleted } from '../services/analytics';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.common.white,
  fontWeight: 'bold',
}));

const ViewPurchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [productNameFilter, setProductNameFilter] = useState('');
  const filterInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        const response = await axiosInstance.get('/purchases');
        setPurchases(response.data);
      } catch (error) {
        console.error('Error fetching purchases:', error);
      }
    };
    fetchPurchases();
  }, []);

  // Focus the filter input when filters are enabled
  useEffect(() => {
    if (showFilters && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [showFilters]);

  // Reset page to 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [showFilters, productNameFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (id) => {
    navigate(`/dashboard/purchases/edit/${id}`);
  };

  const confirmDelete = (purchase) => {
    setPurchaseToDelete(purchase);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/purchases/${purchaseToDelete.purchase_id}`);
      trackPurchaseDeleted(purchaseToDelete.purchase_id);
      setPurchases(purchases.filter((purchase) => purchase.purchase_id !== purchaseToDelete.purchase_id));
      setSnackbarMessage('Purchase deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Error deleting purchase:', error);
      setSnackbarMessage('Error deleting purchase');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDeleteConfirmOpen(false);
    }
  };

  // Helper function to get filtered purchases based on name filter
  const getFilteredPurchases = () => {
    if (!showFilters || !productNameFilter.trim()) {
      return purchases;
    }

    const trimmedFilter = productNameFilter.trim().toLowerCase();
    return purchases.filter(purchase => {
      const searchText = `${purchase.product_name || ''} ${purchase.brand || ''} ${purchase.variety || ''} ${purchase.size || ''} ${purchase.supplier_name || ''}`.toLowerCase();
      return searchText.includes(trimmedFilter);
    });
  };

  const paginatedPurchases = getFilteredPurchases().slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Container sx={{ marginTop: 4 }}>
      <Typography variant="h4" gutterBottom>
        Purchases
      </Typography>

      {/* Filter Controls */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showFilters}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setShowFilters(isChecked);
                if (!isChecked) {
                  setProductNameFilter('');
                }
              }}
              color="primary"
            />
          }
          label="Enable Filters"
        />
        {showFilters && (
          <TextField
            label="Filter by Product Info"
            variant="outlined"
            value={productNameFilter}
            onChange={(e) => setProductNameFilter(e.target.value)}
            placeholder="Search by product name, brand, variety, size, or supplier..."
            sx={{ maxWidth: 400, width: '100%' }}
            inputRef={filterInputRef}
          />
        )}
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3, marginBottom: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Shop Name</StyledTableCell>
              <StyledTableCell>Product Name</StyledTableCell>
              <StyledTableCell>Supplier</StyledTableCell>
              <StyledTableCell>Order Price</StyledTableCell>
              <StyledTableCell>Discount</StyledTableCell>
              <StyledTableCell>Quantity</StyledTableCell>
              <StyledTableCell>Unit</StyledTableCell>
              <StyledTableCell>Purchase Date</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPurchases.map((purchase) => (
              <TableRow key={purchase.purchase_id}>
                <TableCell>{purchase.shop_name}</TableCell>
                <TableCell>
                  {purchase.product_name}
                  {purchase.brand && ` (${purchase.brand})`}
                </TableCell>
                <TableCell>{purchase.supplier_name || '—'}</TableCell>
                <TableCell>{purchase.order_price}</TableCell>
                <TableCell>{purchase.discount || 0}</TableCell>
                <TableCell>{purchase.quantity}</TableCell>
                <TableCell>{purchase.unit_type}</TableCell>
                <TableCell>{new Date(purchase.purchase_date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleEdit(purchase.purchase_id)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => confirmDelete(purchase)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={getFilteredPurchases().length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog for Deletion */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this purchase? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewPurchases;
